/**
 * FounderBanner — persistent top banner rendered site-wide when the current
 * user is the Founder Super Admin account (or impersonating via that account).
 *
 * Spec requirement: "Founder Mode: All features enabled. This account bypasses
 * all access controls."
 */
import React from 'react';
import { useAuth } from '../../AuthContext';
import { useNavigate } from 'react-router-dom';
import { Crown, ShieldCheck, ArrowLeftRight, Loader2 } from 'lucide-react';
import { founderAPI } from '../../api';
import { toast } from 'sonner';

export default function FounderBanner() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [stopping, setStopping] = React.useState(false);
  if (!user) return null;
  const isFounder = !!user.is_founder_account;
  const impersonating = !!user.impersonator_user_id;
  if (!isFounder && !impersonating) return null;

  const stop = async () => {
    setStopping(true);
    try {
      const { data } = await founderAPI.stopImpersonate();
      // AuthContext expects (token, user, session_id)
      // Re-fetch /me after swapping the token; simplest path: full reload.
      // The token has been set via cookie on the server; rely on /me.
      localStorage.setItem('token', data.token);
      toast.success('Back to Founder identity');
      window.location.href = '/founder';
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to stop impersonating');
    } finally { setStopping(false); }
  };

  return (
    <div
      data-testid="founder-banner"
      className={`w-full flex items-center gap-3 px-4 py-2 text-sm font-medium
        ${impersonating
          ? 'bg-amber-600 text-white'
          : 'bg-gradient-to-r from-indigo-700 via-purple-700 to-indigo-700 text-white'}`}
    >
      {impersonating ? (
        <>
          <ArrowLeftRight className="w-4 h-4 flex-shrink-0" />
          <span className="truncate">
            <b>Founder impersonating</b> — acting as <b>{user.email}</b>{user.full_name ? ` (${user.full_name})` : ''}.
            All actions are audit-logged.
          </span>
          <button
            onClick={stop}
            disabled={stopping}
            className="ml-auto bg-white/20 hover:bg-white/30 px-3 py-1 rounded text-xs inline-flex items-center gap-1"
            data-testid="founder-stop-impersonating"
          >
            {stopping ? <Loader2 className="w-3 h-3 animate-spin" /> : <Crown className="w-3 h-3" />}
            Return to Founder
          </button>
        </>
      ) : (
        <>
          <Crown className="w-4 h-4 flex-shrink-0 text-yellow-300" />
          <span className="truncate">
            <b>Founder Mode:</b> All features enabled. This account bypasses all access controls.
          </span>
          <button
            onClick={() => navigate('/founder')}
            className="ml-auto bg-white/15 hover:bg-white/25 px-3 py-1 rounded text-xs inline-flex items-center gap-1"
            data-testid="founder-open-panel"
          >
            <ShieldCheck className="w-3 h-3" /> Founder panel
          </button>
        </>
      )}
    </div>
  );
}
